import React, { useEffect, useState } from 'react';
import * as Bixby from '$/core';
import './CompanyOwnership.scss';
import InputBlock from '$/components/global/inputBlock';
import SearchTextBox, { SearchTextBoxItem } from '$/components/global/searchTextBox';
import { PrivateEquitySponsorService } from '$/services/PrivateEquitySponsorService';
import { PrivateEquitySponsorType } from '$/core/enums';

export interface ICompanyOwnershipProps {
  ownershipList: Array<Bixby.Models.PrivateEquitySponsorsCompany>;
  isEditing: boolean;
  onChange: (updatedOwnershipList: Array<Bixby.Models.PrivateEquitySponsorsCompany>) => void;
  dispatchUpdateDraftCompany?: (companyId: string, updatedCompany: Bixby.Models.JsonPatchOperation[]) => void;
}

const CompanyOwnership: React.FC<ICompanyOwnershipProps> = props => {
  const { ownershipList, onChange, isEditing } = props;

  const [updatedOwnershipList, setUpdatedOwnershipList] = useState<Array<Bixby.Models.PrivateEquitySponsorsCompany>>(
    ownershipList ?? []
  );
  const [sponsorsList, setSponsorsList] = useState<Array<Bixby.Models.PrivateEquitySponsor>>(
    [] as Array<Bixby.Models.PrivateEquitySponsor>
  );

  const privateEquitySponsorTypes = Object.values(PrivateEquitySponsorType).filter(v => isNaN(Number(v)));

  const handleChange = updatedList => {
    setUpdatedOwnershipList(updatedList);
    onChange(updatedList);
  };

  const removeOwnership = ownerId => {
    handleChange(updatedOwnershipList.filter(_ => _.owner_Id != ownerId));
  };

  const addOwnership = (owner: SearchTextBoxItem) => {
    const privateEquitySponsorCompany: Bixby.Models.PrivateEquitySponsorsCompany = {
      id: '',
      // when list is empty null will be assigned, however it will not impact the draft save/publish
      company_Id: updatedOwnershipList[0]?.company_Id,
      owner_Id: owner.id,
      percentage: 0,
      type: PrivateEquitySponsorType.Other,
      owner: {
        id: owner.id,
        name: owner.name,
      },
    };
    const updatedList = [...updatedOwnershipList, privateEquitySponsorCompany];
    handleChange(updatedList);
  };

  const handleTypeChange = (event, ownerId) => {
    const type: PrivateEquitySponsorType = event.target.value;
    const index = updatedOwnershipList.findIndex(_ => _.owner_Id === ownerId);
    updatedOwnershipList[index].type = type;
    // if the current change have type change to 50/50 Split tag then automatically for that sponsor change percentage to 50
    if (getNumericValue(type) === PrivateEquitySponsorType.FiftyFiftySplit) {
      updatedOwnershipList[index].percentage = 50;
    }
    handleChange(updatedOwnershipList);
  };

  const handlePercentageChange = (event, ownerId) => {
    const percentage = event.target.value;
    const index = updatedOwnershipList.findIndex(_ => _.owner_Id === ownerId);
    updatedOwnershipList[index].percentage = percentage === '' ? 0 : percentage;
    // if the current change have percentage change to 50 then automatically for that sponsor change type to 50/50
    if (percentage == 50) updatedOwnershipList[index].type = PrivateEquitySponsorType.FiftyFiftySplit;
    // if the current change have percentage change to less than 50 then automatically for that sponsor
    // change type to Minority
    if (percentage < 50) updatedOwnershipList[index].type = PrivateEquitySponsorType.Minority;
    // if the current change have percentage change to greater than 50 then automatically for that sponsor
    // change type to Majority
    if (percentage > 50) updatedOwnershipList[index].type = PrivateEquitySponsorType.Majority;
    handleChange(updatedOwnershipList);
  };

  // convert numeric values of PrivateEquitySponsors Type, which can be string or numeric
  // Backend returns string and internally on FE it is numeric
  const getNumericValue = (value: PrivateEquitySponsorType) => {
    if (typeof PrivateEquitySponsorType[value] == 'number') return PrivateEquitySponsorType[value];

    return PrivateEquitySponsorType[PrivateEquitySponsorType[value]];
  };

  useEffect(() => {
    const getSponsorsList = async () => {
      const allSponsorsList = await PrivateEquitySponsorService.getPrivateEquitySponsors();
      setSponsorsList(allSponsorsList);
    };
    getSponsorsList();
  }, []);

  const editableView = () => {
    return (
      <>
        {updatedOwnershipList.map((owner, idx) => {
          const ownerTypeNumericValue = getNumericValue(owner.type);
          return (
            <tr key={`ownership-list-${idx}`}>
              <td className="owner-value-cell" title={owner.owner.name}>
                {owner.owner.name}
              </td>
              <td className="owner-value-cell">
                <InputBlock>
                  <select value={ownerTypeNumericValue} onChange={event => handleTypeChange(event, owner.owner_Id)}>
                    {privateEquitySponsorTypes.map(type => {
                      const typeName = type === 'FiftyFiftySplit' ? '50/50 Split' : type;
                      return (
                        <option key={`private-equity-sponsor-type-${typeName}`} value={PrivateEquitySponsorType[type]}>
                          {typeName}
                        </option>
                      );
                    })}
                  </select>
                </InputBlock>
              </td>
              <td className="owner-value-cell">
                <InputBlock>
                  <input
                    type="number"
                    min="0.00"
                    max="100.00"
                    defaultValue={owner.percentage}
                    value={owner.percentage}
                    onChange={event => handlePercentageChange(event, owner.owner_Id)}
                    step="0.01"
                  />
                </InputBlock>
              </td>
              <td className="owner-value-cell td-center">
                <button type="button" className="fa fa-minus-circle" onClick={() => removeOwnership(owner.owner_Id)} />
              </td>
            </tr>
          );
        })}
        <tr>
          <td colSpan={4} className="company-ownership-add-owner">
            <SearchTextBox
              placeholder="Add Owner"
              searchFromList={
                // To get the sponsors list except that already added
                sponsorsList
                  .filter(sponsor => !updatedOwnershipList.some(owner => owner.owner_Id === sponsor.id))
                  .map(sponsor => ({ id: sponsor.id, name: sponsor.name })) as Array<SearchTextBoxItem>
              }
              onSelect={addOwnership}
            />
          </td>
        </tr>
      </>
    );
  };
  const readOnlyView = () => {
    return (
      <>
        {updatedOwnershipList.map((owner, idx) => (
          <tr key={`ownership-read-only-list-${idx}`}>
            <td title={owner.owner.name}>{owner.owner.name}</td>
            <td className="td-center">
              {owner.type === PrivateEquitySponsorType.FiftyFiftySplit ? '50/50 Split' : owner.type.toString()}
            </td>
            <td className="td-center">{owner.percentage}%</td>
          </tr>
        ))}
      </>
    );
  };

  return (
    <>
      {updatedOwnershipList?.length > 0 ? (
        <table id="ownersList">
          <thead>
            <tr>
              <th className="owner-column">Owner</th>
              <th className="owner-column">Type</th>
              <th className="owner-column">Percentage</th>
              {isEditing && <th className="owner-remove-column">Remove</th>}
            </tr>
          </thead>
          <tbody id="domainBody">{isEditing ? editableView() : readOnlyView()}</tbody>
        </table>
      ) : isEditing ? (
        // Case when no ownership is defined and fresh records need to be added
        <table id="ownersList">
          <thead>
            <tr>
              <th className="owner-column">Owner</th>
              <th className="owner-column">Type</th>
              <th className="owner-column">Percentage</th>
              {isEditing && <th className="owner-remove-column">Remove</th>}
            </tr>
          </thead>
          <tbody id="domainBody">{editableView()}</tbody>
        </table>
      ) : (
        <div>
          <span>No records found</span>
        </div>
      )}
    </>
  );
};

export default CompanyOwnership;
